import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BarChart3, Bell, BriefcaseBusiness, Building2, CalendarCheck, CheckSquare2, ChevronDown,
  Clock3, FileSpreadsheet, FileText, HeartHandshake, LayoutDashboard, Mail, Mic, Presentation, QrCode,
  Settings2, ShieldCheck, Sparkles, Users, Wrench,
} from 'lucide-react'
import { getActiveNavigationPath, type NavigationGroup, type NavigationIcon } from '@/lib/workspace-navigation'
import { cn } from '@/lib/utils'

const icons: Record<NavigationIcon, typeof LayoutDashboard> = {
  dashboard: LayoutDashboard, tasks: CheckSquare2, clock: Clock3, calendar: CalendarCheck,
  bell: Bell, sparkles: Sparkles, users: Users, heart: HeartHandshake, building: Building2,
  briefcase: BriefcaseBusiness, file: FileText, spreadsheet: FileSpreadsheet, chart: BarChart3,
  shield: ShieldCheck, settings: Settings2, wrench: Wrench, qr: QrCode, mail: Mail, mic: Mic,
  presentation: Presentation,
}

interface WorkspaceNavigationProps {
  groups: NavigationGroup[]
  pathname: string
  navigationKey: string
  collapsed?: boolean
  onExpand: () => void
  onNavigate: () => void
}

export function WorkspaceNavigation({ groups, pathname, navigationKey, collapsed = false, onExpand, onNavigate }: WorkspaceNavigationProps) {
  const id = useId()
  const activePath = getActiveNavigationPath(groups, pathname)
  const activeGroup = groups.find((group) => group.items.some((item) => item.to === activePath))?.id
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => activeGroup ? [activeGroup] : [])

  useEffect(() => {
    if (!activeGroup) return
    setExpandedGroups((previous) => previous.includes(activeGroup) ? previous : [...previous, activeGroup])
  }, [activeGroup, navigationKey])

  return (
    <nav aria-label="Main navigation" className="custom-scrollbar flex-1 overflow-y-auto p-3">
      <div className="space-y-4">
        {groups.map((group) => {
          const GroupIcon = icons[group.icon]
          const isActiveGroup = group.id === activeGroup
          const expanded = Boolean(group.alwaysOpen || (!collapsed && expandedGroups.includes(group.id)))
          const groupId = `${id}-${group.id}`

          return (
            <div key={group.id} className="space-y-1">
              {group.alwaysOpen ? (
                <p className={cn('px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400', collapsed && 'sr-only')}>
                  {group.label}
                </p>
              ) : (
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={groupId}
                  aria-label={collapsed ? group.label : undefined}
                  title={collapsed ? group.label : undefined}
                  onClick={() => {
                    if (collapsed) {
                      onExpand()
                      setExpandedGroups((previous) => previous.includes(group.id) ? previous : [...previous, group.id])
                    } else {
                      setExpandedGroups((previous) => previous.includes(group.id)
                        ? previous.filter((value) => value !== group.id)
                        : [...previous, group.id])
                    }
                  }}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-400 transition hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    isActiveGroup && 'bg-white/5 text-slate-100',
                    collapsed && 'justify-center px-2',
                  )}
                >
                  <GroupIcon aria-hidden="true" className="size-5 shrink-0" />
                  {!collapsed && <>
                    <span className="min-w-0 flex-1">{group.label}</span>
                    <ChevronDown aria-hidden="true" className={cn('size-4 shrink-0 transition-transform', !expanded && '-rotate-90')} />
                  </>}
                  {isActiveGroup && <span className="sr-only">Contains current page</span>}
                </button>
              )}
              <ul id={groupId} hidden={!expanded} className={cn('space-y-1', !group.alwaysOpen && 'ml-3 border-l border-white/10 pl-2')}>
                {group.items.map(({ to, label, icon }) => {
                  const Icon = icons[icon]
                  const isActive = activePath === to
                  return (
                    <li key={to}>
                      <Link
                        to={to}
                        aria-current={isActive ? 'page' : undefined}
                        title={collapsed ? label : undefined}
                        onClick={onNavigate}
                        className={cn(
                          'group flex min-h-11 items-center gap-3 rounded-lg border-l-2 border-transparent px-3 py-2.5 text-sm font-medium text-slate-400 transition hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                          isActive && 'border-l-primary bg-primary/10 font-semibold text-primary',
                          collapsed && 'justify-center px-2',
                        )}
                      >
                        <Icon aria-hidden="true" className="size-5 shrink-0" />
                        <span className={collapsed ? 'sr-only' : 'truncate'}>{label}</span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </div>
    </nav>
  )
}
